<template>
    <kgr-form-scaffold
        :pending="pending || loading"
        :with-back-button="false"
        :with-primary-buttons="true"
        @primaryBtnClicked="update()"
    >
        <kgr-loader v-if="loading" />
        <div
            v-else-if="!loading && clubSalesListOfferingConfig && clubFeeCategories"
            class="kgr-inputGroup"
        >
            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ $t('Angebotsartikel') }}
            </h3>
            <div
                class="kgr-alert kgr-alert--info kgr-row"
                v-html="$t('club.salesListOfferingConfig.items.infoText')"
            ></div>
            <div class="kgr-grid__cell">
                <kgr-form-row
                    :label="$t('Bitte wähle eine oder mehrere Kategorien')"
                    :error="error"
                    field-name="clubFeeCategories"
                >
                    <kgr-multi-select
                        v-model="selectedCategories"
                        :options="filteredCategories"
                        display="name"
                        searchable
                    />
                </kgr-form-row>
            </div>
            <hr class="kgr-separator" />
            <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ $t('Übersicht der ausgewählten Artikel') }}
            </h3>
            <table
                vue:is="kgr-table"
                class="kgr-table kgr-table--striped kgr-table--block"
                :is-data-table="false"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('Name') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('Kategorie') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('zu zahlen') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Preis') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in offeringItems"
                        :key="item.id"
                        class="kgr-table_row"
                    >
                        <td class="kgr-table__cell">
                            {{ item.name }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ item.category.name }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ translatePaymentApproach(item.paymentApproach) }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-price
                                :price="item.pricePerTime"
                                with-vat
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </kgr-form-scaffold>
    <kgr-generic-modal
        :value="showConfirmModal"
        :headline="$t('Bist du dir sicher?')"
        :width-in-px="600"
        @post-input="showConfirmModal = false"
    >
        <div class="mb1">{{ $t('Bei Entfernung einer oder mehrerer Kategorien stehen bereits verwendete Artikel weiterhin in der Buchung des Angebots zur Verfügung, können dort aber nicht mehr bearbeitet oder entfernt werden.') }}</div>
        <div>{{ $t('Die Artikel der Kategorie stehen bei neuen Angeboten nicht mehr zur Verfügung.') }}</div>
        <template #footer>
            <kgr-button
                class="kgr-col"
                :value="$t('Speichern')"
                @click="confirmUpdate()"
            />
            <kgr-button
                type="grey"
                :value="$t('cancel')"
                @click="rejectUpdate()"
            />
        </template>
    </kgr-generic-modal>
</template>

<script>
import Button from '../../../components/common/form/button/KgrButton.vue';
import ClubFeeCategoryRepository from '../../../js/repository/ClubFeeCategoryRepository.js';
import ClubSalesListOfferingConfigRepository from '../../../js/repository/club/ClubSalesListOfferingConfigRepository.js';
import ClubSalesListOfferingItemRepository from '../../../js/repository/club/ClubSalesListOfferingItemRepository.js'
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import FormScaffold from '../../../components/common/form/FormScaffold.vue';
import GenericModal from '../../../components/common/modal/GenericModal.vue';
import Loading from '../../../components/common/Loading.vue';
import MultiSelect from '../../../components/common/form/select/MultiSelect.vue';
import PricePerTimeOutput from '../../../components/club/sales-list/PricePerTimeOutput.vue';

export default {
    components: {
        'kgr-button': Button,
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-generic-modal': GenericModal,
        'kgr-loader': Loading,
        'kgr-multi-select': MultiSelect,
        'kgr-price': PricePerTimeOutput,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            pending: false,
            loading: false,
            clubFeeCategories: null,
            clubSalesListOfferingConfig: null,
            selectedCategories: [],
            initialCategoryIds: [],
            offeringItems: [],
            showConfirmModal: false,
        }
    },
    computed: {
        filteredCategories() {
            if (!this.clubFeeCategories || this.clubSalesListOfferingConfig === null) {
                return [];
            }
            return this.clubFeeCategories.filter(el => {
                return this.selectedCategories.filter(selected => selected.id === el.id).length === 0;
            });
        },
        haveCategoriesBeenRemoved() {
            const currentIds = this.selectedCategories.map(c => c.id);
            return this.initialCategoryIds.some(id => !currentIds.includes(id));
        },
    },
    methods: {
        loadClubFeeCategories () {
            ClubFeeCategoryRepository.findByClubId(this.clubId)
                .then(response => {
                    this.clubFeeCategories = response
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        loadClubSalesListOfferingConfig(loading = false) {
            this.loading = loading;
            ClubSalesListOfferingConfigRepository.findByClubId(this.clubId)
                .then(response => {
                    this.clubSalesListOfferingConfig = response;
                    this.selectedCategories = this.clubSalesListOfferingConfig.clubFeeCategories;
                    this.initialCategoryIds = this.selectedCategories.map(c => c.id).sort();
                    this.loadOfferingItems();
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        update() {
            if (this.haveCategoriesBeenRemoved) {
                this.showConfirmModal = true;
                return;
            }
            this.performUpdate();
        },
        confirmUpdate() {
            this.showConfirmModal = false;
            this.performUpdate();
        },
        rejectUpdate() {
            this.showConfirmModal = false;
            this.selectedCategories = this.clubSalesListOfferingConfig.clubFeeCategories;
        },
        performUpdate() {
            this.pending = true;
            this.error = new Error();
            this.clubSalesListOfferingConfig.clubFeeCategories = this.selectedCategories;
            ClubSalesListOfferingConfigRepository.update(this.clubId, this.clubSalesListOfferingConfig)
                .then(() => {
                    this.loadClubSalesListOfferingConfig();
                    this.$notify.success('saveChanges.success');
                })
                .catch(error => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => this.pending = false);
        },
        loadOfferingItems() {
            ClubSalesListOfferingItemRepository.findByClubId(this.clubId)
                .then(response => this.offeringItems = response)
                .catch(() => this.$notify.error('error.loadingData'));
        },
        translatePaymentApproach(paymentApproach) {
            switch (paymentApproach) {
                case 'in_advance':
                    return this.$t('vorschüssig');
                case 'in_arrears':
                    return this.$t('nachschüssig');
                case 'undefined':
                    return this.$t('nach Kategorie');
                default:
                    return paymentApproach;
            }
        },
    },
    created() {
        this.loadClubFeeCategories();
        this.loadClubSalesListOfferingConfig(true);
    }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">

</style>
